import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { CategoryDefDto, CategoryDefInput, FieldDef } from '@tww/shared';
import { FIELD_TYPES } from '@tww/shared';
import { api, ApiRequestError } from '../../api/client';
import { useCategoryDefs, useLookups } from '../../api/hooks';
import { ErrorState, Skeleton } from '../../ui/basics';
import { Icon } from '../../ui/Icon';
import { Checkbox, TextInput } from '../../ui/fields';
import { useToast } from '../../ui/Toast';
import { KIND_LABEL, moveItem, slugKey, uniqueKey, useUnsaved } from './adminUtil';
import { Dropdown } from '../../ui/Dropdown';

const TYPE_LABEL: Record<string, string> = { text: 'Text', number: 'Zahl', bool: 'Ja/Nein', select: 'Auswahl', multiselect: 'Mehrfachauswahl', textarea: 'Textbereich', date: 'Datum' };
const ERR_LABEL: Record<string, string> = { key: 'Schlüssel', name: 'Name', name_plural: 'Mehrzahl', kinds_allowed: 'Erlaubt für', fields: 'Zusatzfelder' };

/** Zusatzfeld im Formular; manual = Schlüssel wurde unter „Erweitert“ von Hand gesetzt */
type FieldRow = FieldDef & { manual?: boolean };

/** Kontaktkategorie anlegen (/verwaltung/kontaktkategorien/neu) und bearbeiten (/verwaltung/kontaktkategorien/:key) */
export function CategoryFormPage() {
  const { key } = useParams();
  const defs = useCategoryDefs();
  if (key && defs.isLoading) return <Skeleton rows={8} />;
  if (defs.isError) return <ErrorState error={defs.error} retry={() => defs.refetch()} />;
  const def = key ? (defs.data?.find((d) => d.key === key) ?? null) : null;
  if (key && !def) return <ErrorState error={new Error('Kategorie nicht gefunden')} />;
  return <CategoryForm key={key ?? 'neu'} def={def} taken={(defs.data ?? []).map((d) => d.key)} />;
}

function CategoryForm({ def, taken }: { def: CategoryDefDto | null; taken: string[] }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const lookups = useLookups();
  const initial = useMemo<CategoryDefInput>(
    () =>
      def
        ? { key: def.key, name: def.name, name_plural: def.name_plural, sort: def.sort, icon: def.icon, kinds_allowed: def.kinds_allowed, rating_enabled: def.rating_enabled, fields: def.fields, is_active: def.is_active }
        : { key: '', name: '', name_plural: '', sort: 100, icon: null, kinds_allowed: ['person', 'organisation'], rating_enabled: true, fields: [], is_active: true },
    [def],
  );
  const [f, setF] = useState<Omit<CategoryDefInput, 'fields'> & { fields: FieldRow[] }>(initial);
  const [keyTouched, setKeyTouched] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const savedKeys = new Set((def?.fields ?? []).map((x) => x.key));
  const isSavedField = (x: FieldRow) => Boolean(x.system) || savedKeys.has(x.key);
  const dirty = JSON.stringify({ ...f, fields: f.fields.map(({ manual: _m, ...x }) => x) }) !== JSON.stringify(initial);
  useUnsaved(dirty);

  const save = useMutation({
    mutationFn: () => {
      const fields: FieldDef[] = f.fields.map(({ manual: _m, ...x }) => x);
      return def ? api.patch(`/contact-categories/${def.key}`, { name: f.name, name_plural: f.name_plural, kinds_allowed: f.kinds_allowed, rating_enabled: f.rating_enabled, fields }) : api.post('/contact-categories', { ...f, fields });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contact-categories'] });
      toast(def ? 'Kategorie gespeichert' : 'Kategorie angelegt');
      nav('/verwaltung/kontaktkategorien');
    },
    onError: (e) => {
      if (e instanceof ApiRequestError) {
        setErrors([e.message, ...Object.entries(e.fields ?? {}).map(([k, v]) => `${ERR_LABEL[k.split('.')[0]!] ?? k}: ${v}`)]);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else toast('Speichern fehlgeschlagen', 'error');
    },
  });

  const setFields = (fields: FieldRow[]) => setF((s) => ({ ...s, fields }));
  const setField = (i: number, patch: Partial<FieldRow>) => setFields(f.fields.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const setLabel = (i: number, label: string) => {
    const x = f.fields[i]!;
    const auto = !isSavedField(x) && !x.manual;
    setField(i, { label, key: auto ? uniqueKey(slugKey(label), f.fields.filter((_, j) => j !== i).map((y) => y.key)) : x.key });
  };
  const addField = () => setFields([...f.fields, { key: uniqueKey('feld', f.fields.map((y) => y.key)), label: '', type: 'text' }]);
  const lists = (lookups.data ?? []).map((l) => ({ value: l.key, label: l.name }));
  const canSave = f.name.trim() && f.name_plural.trim() && f.key && f.kinds_allowed.length && f.fields.every((x) => x.label.trim() && x.key);
  const back = '/verwaltung/kontaktkategorien';

  return (
    <>
      <div className="crumb">
        <Link to="/verwaltung">Verwaltung</Link> / <Link to={back}>Kontaktkategorien</Link>
      </div>
      <div className="head">
        <div>
          <h1>{def ? `Kategorie „${def.name}“` : 'Neue Kontaktkategorie'}</h1>
          {def?.is_system && <div className="sub">Systemkategorie: Systemfelder bleiben erhalten, weitere Felder lassen sich ergänzen.</div>}
        </div>
      </div>
      {errors.length > 0 && (
        <div className="form-errors" role="alert">
          Bitte prüfen:
          <ul>
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </div>
      )}
      <form
        className="page-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSave) save.mutate();
        }}
      >
        <div className="col">
          <section className="section">
            <div className="sh">
              <h2>Kategorie</h2>
            </div>
            <div className="sb">
              <div className="form">
                <TextInput label="Name (Einzahl)" value={f.name} onChange={(v) => setF({ ...f, name: v, key: def || keyTouched ? f.key : uniqueKey(slugKey(v, 40, ''), taken), name_plural: f.name_plural && f.name_plural !== f.name ? f.name_plural : v })} required autoFocus={!def} placeholder="Presse" />
                <TextInput label="Name (Mehrzahl)" value={f.name_plural} onChange={(v) => setF({ ...f, name_plural: v })} required placeholder="Presse" />
                <div className="field">
                  <label>Erlaubt für</label>
                  <ul className="check-list no-date compact">
                    {(['person', 'organisation'] as const).map((k) => (
                      <li key={k} className={f.kinds_allowed.includes(k) ? 'on' : ''}>
                        <label>
                          <input type="checkbox" checked={f.kinds_allowed.includes(k)} onChange={(e) => setF({ ...f, kinds_allowed: e.target.checked ? [...f.kinds_allowed, k] : f.kinds_allowed.filter((x) => x !== k) })} />
                          <span>{KIND_LABEL[k]}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="field">
                  <label>Bewertung</label>
                  <Checkbox label="A/B/C möglich" value={f.rating_enabled} onChange={(v) => setF({ ...f, rating_enabled: v })} />
                </div>
              </div>
            </div>
          </section>

          <section className="section">
            <div className="sh">
              <h2>Zusatzfelder</h2>
            </div>
            {f.fields.length > 0 && (
              <div className="tw">
                <table>
                  <thead>
                    <tr>
                      <th className="col-sort">
                        <span className="sr-only">Reihenfolge</span>
                      </th>
                      <th>Bezeichnung</th>
                      <th>Typ</th>
                      <th>Optionen / Stammliste</th>
                      <th>Abschnitt</th>
                      <th>Pflicht</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {f.fields.map((fd, i) => (
                      <tr key={i}>
                        <td className="sort-cell">
                          <button type="button" className="sort-btn" onClick={() => setFields(moveItem(f.fields, i, i - 1))} disabled={i === 0} aria-label={`${fd.label || 'Feld'} nach oben`} title="Nach oben">
                            ↑
                          </button>
                          <button type="button" className="sort-btn" onClick={() => setFields(moveItem(f.fields, i, i + 1))} disabled={i === f.fields.length - 1} aria-label={`${fd.label || 'Feld'} nach unten`} title="Nach unten">
                            ↓
                          </button>
                        </td>
                        <td>
                          <input className="tbl-input" value={fd.label} onChange={(e) => setLabel(i, e.target.value)} aria-label="Bezeichnung" placeholder="z. B. Redaktion" />
                          {fd.system && <span className="sm">Systemfeld</span>}
                        </td>
                        <td>
                          <Dropdown value={fd.type} onChange={(v) => setField(i, { type: v as FieldDef['type'] })} disabled={fd.system} aria-label="Typ" size="sm" clearable={false} options={FIELD_TYPES.map((t) => ({ value: String(t), label: TYPE_LABEL[t] }))} />
                        </td>
                        <td>
                          {(fd.type === 'select' || fd.type === 'multiselect') && (
                            <div className="tbl-stack">
                              <Dropdown value={fd.lookup ?? ''} onChange={(v) => setField(i, { lookup: v || undefined })} aria-label="Stammliste" size="sm" placeholder="eigene Optionen" options={lists.map((l) => ({ value: String(l.value), label: l.label }))} />
                              {!fd.lookup && <input className="tbl-input" value={(fd.options ?? []).join(', ')} onChange={(e) => setField(i, { options: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} placeholder="Option A, Option B" aria-label="Optionen" />}
                            </div>
                          )}
                        </td>
                        <td>
                          <input className="tbl-input" value={fd.section ?? ''} onChange={(e) => setField(i, { section: e.target.value || undefined })} aria-label="Abschnitt" placeholder="optional" />
                        </td>
                        <td className="center">
                          <input type="checkbox" checked={Boolean(fd.required)} onChange={(e) => setField(i, { required: e.target.checked })} aria-label="Pflicht" />
                        </td>
                        <td>
                          {!fd.system && (
                            <button className="icon-btn" type="button" aria-label="Feld entfernen" title="Feld entfernen" onClick={() => setFields(f.fields.filter((_, j) => j !== i))}>
                              <Icon name="trash" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="sb">
              <button className="btn sm" type="button" onClick={addField}>
                <Icon name="plus" /> Feld
              </button>
            </div>
          </section>

          <details className="fs section">
            <summary>Erweitert</summary>
            <div className="body">
              <div className="form one">
                <TextInput label="Schlüssel der Kategorie" value={f.key} onChange={(v) => (setKeyTouched(true), setF({ ...f, key: v }))} disabled={Boolean(def)} hint={def ? 'Fest, wird bei Import und Auswertung verwendet' : 'Wird aus dem Namen erzeugt und bleibt nach dem Anlegen fest'} />
                {f.fields.length > 0 && (
                  <div className="field">
                    <label>Schlüssel der Zusatzfelder</label>
                    <div className="key-list">
                      {f.fields.map((fd, i) => (
                        <label key={i} className="key-row">
                          <span>{fd.label || 'ohne Bezeichnung'}</span>
                          <input className="tbl-input" value={fd.key} onChange={(e) => setField(i, { key: e.target.value, manual: true })} disabled={isSavedField(fd)} aria-label={`Schlüssel für ${fd.label}`} />
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </details>
        </div>

        <aside className="aside">
          <div className="section">
            <div className="sh">
              <h2>Zusammenfassung</h2>
            </div>
            <div className="sb summary-box">
              <strong>{f.name || <span className="mut">Noch kein Name</span>}</strong>
              <dl>
                <dt>Erlaubt für</dt>
                <dd>{f.kinds_allowed.length ? f.kinds_allowed.map((k) => KIND_LABEL[k]).join(', ') : <span className="miss">mindestens eins wählen</span>}</dd>
                <dt>Bewertung</dt>
                <dd>{f.rating_enabled ? 'A/B/C möglich' : 'keine'}</dd>
                <dt>Zusatzfelder</dt>
                <dd>{f.fields.length ? `${f.fields.length}, davon ${f.fields.filter((x) => x.required).length} Pflicht` : 'keine'}</dd>
              </dl>
            </div>
          </div>
          <div className="actions-col">
            <button className="btn primary" type="submit" disabled={!canSave || !dirty || save.isPending}>
              {save.isPending ? 'Wird gespeichert …' : def ? 'Änderungen speichern' : 'Kategorie anlegen'}
            </button>
            <Link className="btn" to={back}>
              Abbrechen
            </Link>
          </div>
        </aside>
      </form>
    </>
  );
}
